import { useCallback } from 'react';
import useStore from '../context/useStore';
import moment from 'moment-timezone';

type FormatCurrencyOptions = {
  currency?: string;
  locale?: string;
  trimInteger?: boolean;
};

export default function useHelpers() {
  const { storeData } = useStore();

  const formatCurrency = useCallback(
    (amount: number, options?: FormatCurrencyOptions) => {
      const { currency = null, locale = null, trimInteger = false } = options ?? {};

      const _currency = currency ?? storeData.currency ?? 'EUR';
      const _locale =
        locale ??
        {
          EUR: 'de-DE',
          USD: 'en-US',
          GBP: 'en-UK',
        }[_currency];

      return new Intl.NumberFormat(_locale, {
        style: 'currency',
        currency: _currency,
        minimumFractionDigits: Number.isInteger(amount) && trimInteger ? 0 : undefined,
      }).format(amount);
    },
    [storeData.currency],
  );

  const formatDate = useCallback(
    (datetime: string, format = undefined) => {
      const _format = format ?? (storeData.location.country === 'US' ? 'MM-DD-YYYY' : 'DD.MM.YYYY');

      return moment(datetime).format(_format);
    },
    [storeData.location.country],
  );

  const formatTime = useCallback(
    (datetime: string, format = undefined) => {
      const _format = format ?? (storeData.location.country === 'US' ? 'h:mm a' : 'HH:mm');

      return moment(datetime).format(_format);
    },
    [storeData.location.country],
  );

  const formatDateTime = useCallback(
    (
      datetime: string,
      { format = undefined, dateFormat = undefined, timeFormat = undefined } = {},
    ) => {
      const _dateFormat =
        dateFormat ?? (storeData.location.country === 'US' ? 'MM-DD-YYYY' : 'DD.MM.YYYY');
      const _timeFormat = timeFormat ?? (storeData.location.country === 'US' ? 'h:mm a' : 'HH:mm');
      const _format = format ?? `${_dateFormat} ${_timeFormat}`.trim();

      return moment(datetime).format(_format);
    },
    [storeData.location.country],
  );

  return {
    formatCurrency,
    formatDate,
    formatTime,
    formatDateTime,
  };
}
